import { i18n } from '../services/i18n.js';
import { dbSim } from '../services/dbSim.js';
import { escapeHTML } from '../services/sanitize.js';
import { icon } from '../services/icons.js';

export const GameEditorView = async (container, params) => {
  // Render loading state
  container.innerHTML = `<div class="container" style="padding: 40px; text-align:center; color:var(--color-text-secondary);">${i18n.t('loading_game')}</div>`;

  // Auth Guard
  if (!localStorage.getItem("h150_auth_user")) {
    window.location.hash = '#/login';
    return;
  }

  const isEdit = !!params.id;
  let game = null;
  
  if (isEdit) {
    try {
      game = await dbSim.getGame(params.id);
    } catch (err) {
      console.error(err);
    }
  }

  if (isEdit && !game) {
    window.showToast("Trò chơi không tồn tại hoặc đã bị xóa!");
    window.location.hash = '#/dashboard';
    return;
  }

  if (!game) {
    game = {
      id: "game_" + Math.random().toString(36).substr(2, 9),
      title: "",
      description: "",
      coverImage: "assets/game_cover.png",
      timeLimit: 15,
      requirePlayerLogin: false,
      questions: []
    };
  }

  const hostTier = dbSim.getHostTier();
  const MAX_QUESTIONS = 15;
  const RECOMMENDED_QUESTIONS = 10;
  if (game.requirePlayerLogin === undefined) game.requirePlayerLogin = false;
  let basicEditing = false;
  const editingQuestions = new Set();
  let basicOpen = false;
  const expandedQuestions = new Set();
  const activeFields = new Set();
  const coverChoices = ['assets/game_cover.png', 'assets/hero_crowd.png', 'assets/demo_mockup.png'];
  if (!coverChoices.includes(game.coverImage)) game.coverImage = coverChoices[0];

  // If new game, add one default question
  if (game.questions.length === 0) {
    game.questions.push({
      id: "q_" + Math.random().toString(36).substr(2, 9),
      type: "multiple_choice",
      questionText: "",
      timeLimit: 15,
      options: [
        { id: "A", text: "" },
        { id: "B", text: "" },
        { id: "C", text: "" },
        { id: "D", text: "" }
      ],
      correctOptions: ["A"],
      explanation: ""
    });
  }

  const render = () => {
    const textAccess = key => basicEditing && activeFields.has(key) ? '' : 'readonly aria-readonly="true"';
    const controlAccess = key => basicEditing && activeFields.has(key) ? '' : 'disabled';
    const hasEditingSection = basicEditing || editingQuestions.size > 0;
    container.innerHTML = `
      <div class="container editor-shell" style="padding: 40px 24px; max-width:800px;">
        <div class="editor-page-header">
          <div><h1 style="font-size:24px; font-weight:800;">${isEdit ? i18n.t('edit_game_title') : i18n.t('create_game_title')}</h1><p class="editor-mode-help">${i18n.t('editing_mode_off_help')}</p></div>
          <div class="editor-page-actions">
            ${hasEditingSection ? `<button id="saveGameBtn" class="btn btn-primary">${i18n.t('save_game')}</button>` : ''}
          </div>
        </div>

        ${isEdit ? `<section class="game-session-actions" aria-labelledby="session-actions-title">
          <div><span>${i18n.t('session_actions_kicker')}</span><h2 id="session-actions-title">${i18n.t('start_session')}</h2><p>${i18n.t('session_actions_help')}</p></div>
          <div class="game-session-buttons">
            <button class="btn btn-secondary session-btn" data-mode="preview">${icon('sparkles')} ${i18n.t('try_game')}</button>
            <button class="btn btn-primary session-btn" data-mode="live">${icon('bolt')} ${i18n.t('play_live')}</button>
          </div>
        </section>` : ''}

        <!-- Meta Info Card -->
        <details class="editor-basic-panel" ${basicOpen ? 'open' : ''}>
          <summary><span><strong>${i18n.t('basic_info')}</strong><small>${escapeHTML(game.title || i18n.t('untitled_quiz'))}</small></span><span class="section-summary-actions">${icon('chevronDown')}</span></summary>
          <div class="editor-basic-content ${basicEditing ? 'section-is-editing' : ''}">
          <div class="section-edit-toolbar"><label class="editing-mode-control editing-mode-compact" for="basicEditingModeSwitch"><span>${i18n.t('editing_mode')}</span><input id="basicEditingModeSwitch" type="checkbox" ${basicEditing ? 'checked' : ''}><i aria-hidden="true"></i></label></div>
          
          <div class="form-group">
            <label class="form-label">${i18n.t('game_name')}</label>
            <div class="inline-edit-field" data-edit-field="basic.title"><input type="text" id="gameTitleInput" class="input-field" value="${escapeHTML(game.title)}" placeholder="${i18n.t('game_name_placeholder')}" ${textAccess('basic.title')} required /></div>
          </div>

          <div class="form-group">
            <label class="form-label">${i18n.t('short_description')}</label>
            <div class="inline-edit-field" data-edit-field="basic.description"><textarea id="gameDescInput" class="input-field" style="height:80px; resize:none;" placeholder="${i18n.t('description_placeholder')}" ${textAccess('basic.description')}>${escapeHTML(game.description)}</textarea></div>
          </div>

          <div class="editor-meta-grid">
            <div class="form-group">
              <label class="form-label">${i18n.t('default_time')}</label>
              <div class="inline-edit-field" data-edit-field="basic.time"><select id="gameTimeLimitSelect" class="input-field" ${controlAccess('basic.time')}>
                <option value="10" ${game.timeLimit == 10 ? 'selected' : ''}>10s</option>
                <option value="15" ${game.timeLimit == 15 ? 'selected' : ''}>15s</option>
                <option value="20" ${game.timeLimit == 20 ? 'selected' : ''}>20s</option>
                <option value="30" ${game.timeLimit == 30 ? 'selected' : ''}>30s</option>
                <option value="60" ${game.timeLimit == 60 ? 'selected' : ''}>60s</option>
              </select></div>
            </div>
            
          </div>

          <div class="form-group">
            <label class="form-label">${i18n.t('choose_cover')}</label>
            <div class="inline-edit-field" data-edit-field="basic.cover"><div class="cover-choice-grid">${coverChoices.map((cover, index) => `<label class="cover-choice ${game.coverImage === cover ? 'selected' : ''}"><input type="radio" name="gameCover" value="${cover}" ${game.coverImage === cover ? 'checked' : ''} ${controlAccess('basic.cover')}><img src="${cover}" alt="${i18n.t('cover_sample')} ${index + 1}"><span>${i18n.t('cover_sample')} ${index + 1}</span></label>`).join('')}</div></div>
          </div>

          <div class="form-group inline-edit-field" data-edit-field="basic.login" style="margin-top:16px; display:flex; align-items:center; gap:10px;">
            <input type="checkbox" id="requirePlayerLoginChk" ${game.requirePlayerLogin ? 'checked' : ''} ${controlAccess('basic.login')} style="width:18px; height:18px; accent-color:var(--color-primary); cursor:pointer;" />
            <label for="requirePlayerLoginChk" style="font-size:14px; font-weight:600; cursor:pointer; display:flex; align-items:center; gap:8px;">
              ${i18n.t('require_google_login')}
              <span class="badge badge-green" style="font-size:10px; padding:2px 6px;">${i18n.t('optional')}</span>
            </label>
          </div>
          </div>
        </details>

        <details class="editor-ai-panel ${basicEditing ? 'section-is-editing' : 'is-readonly'}">
          <summary><span>${icon('sparkles')} ${i18n.t('ai_generate')}</span><span>${i18n.t('optional')}</span></summary>
          <div class="editor-ai-content">
            <div class="form-group">
              <label class="form-label" for="aiTopicInput">${i18n.t('ai_topic')}</label>
              <div class="inline-edit-field" data-edit-field="ai.topic"><textarea id="aiTopicInput" class="input-field" maxlength="500" rows="3" placeholder="${i18n.t('ai_topic_placeholder')}" ${textAccess('ai.topic')}></textarea></div>
            </div>
            <div class="editor-meta-grid">
              <div class="form-group">
                <label class="form-label" for="aiQuestionCount">${i18n.t('question_count')}</label>
                <div class="inline-edit-field" data-edit-field="ai.count"><select id="aiQuestionCount" class="input-field" ${controlAccess('ai.count')}>
                  <option value="3">3</option><option value="5" selected>5</option><option value="8">8</option><option value="10">10</option>
                </select></div>
              </div>
              <div class="form-group">
                <label class="form-label" for="aiDifficulty">${i18n.t('difficulty')}</label>
                <div class="inline-edit-field" data-edit-field="ai.difficulty"><select id="aiDifficulty" class="input-field" ${controlAccess('ai.difficulty')}>
                  <option value="easy">${i18n.t('easy')}</option><option value="medium" selected>${i18n.t('medium')}</option><option value="hard">${i18n.t('hard')}</option>
                </select></div>
              </div>
            </div>
            <button id="generateQuizBtn" class="btn btn-secondary" ${basicEditing ? '' : 'disabled'}>${icon('sparkles')} ${i18n.t('generate_draft')}</button>
            <p class="form-help">${i18n.t('ai_draft_help')}</p>
          </div>
        </details>

        <!-- Questions Editor Area -->
        <div id="questionsContainer">
          <!-- Dynamic rendering here -->
        </div>

        <!-- Question limits enforcement -->
        ${!basicEditing ? '' : game.questions.length >= MAX_QUESTIONS ? `
          <div style="color:var(--color-danger); font-weight:700; text-align:center; margin-top:20px; padding:12px; background:rgba(239,68,68,0.05); border-radius:var(--radius-md); border:1px solid rgba(239,68,68,0.15)">
            ${icon('xCircle')} ${i18n.t('max_questions')}
          </div>
        ` : `
          <button id="addQuestionBtn" class="btn btn-secondary" style="width:100%; border-style:dashed; margin-top:16px; display:flex; justify-content:center;">
            ${icon('plus')} ${i18n.t('add_question')}
          </button>
        `}
      </div>
    `;

    renderQuestions();

    // Event listeners
    const basicEditingSwitch = container.querySelector('#basicEditingModeSwitch');
    basicEditingSwitch.addEventListener('change', event => {
      if (!event.target.checked && basicEditing) syncGameDraft();
      basicEditing = event.target.checked;
      if (basicEditing) basicOpen = true;
      if (!basicEditing) [...activeFields].filter(key => key.startsWith('basic.') || key.startsWith('ai.')).forEach(key => activeFields.delete(key));
      render();
    });

    const basicPanel = container.querySelector('.editor-basic-panel');
    basicPanel.addEventListener('toggle', () => { basicOpen = basicPanel.open; });
    container.querySelectorAll('input[name="gameCover"]').forEach(input => input.addEventListener('change', event => {
      game.coverImage = event.target.value;
      container.querySelectorAll('.cover-choice').forEach(choice => choice.classList.toggle('selected', choice.contains(event.target)));
    }));
    container.querySelectorAll('[data-edit-field]').forEach(field => field.addEventListener('click', event => {
      if (!field.closest('.section-is-editing') || activeFields.has(field.dataset.editField)) return;
      event.preventDefault();
      activeFields.add(field.dataset.editField);
      field.classList.add('is-active');
      const controls = field.querySelectorAll('input,textarea,select');
      controls.forEach(control => { control.readOnly = false; control.disabled = false; control.setAttribute('aria-readonly', 'false'); });
      controls[0]?.focus();
    }));

    container.querySelector('#saveGameBtn')?.addEventListener('click', () => {
      saveGameData();
    });

    container.querySelectorAll('.session-btn').forEach(button => button.addEventListener('click', async event => {
      try {
        const mode = event.currentTarget.dataset.mode;
        const session = await dbSim.createSession(game.id, mode);
        window.showToast(i18n.t(mode === 'preview' ? 'preview_created' : 'live_created'));
        window.location.hash = `#/sessions/${session.id}/lobby`;
      } catch (error) { window.showToast(error.message); }
    }));

    const addQBtn = container.querySelector('#addQuestionBtn');
    if (addQBtn) {
      addQBtn.addEventListener('click', () => {
        addQuestion();
      });
    }

    container.querySelector('#generateQuizBtn')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const topic = container.querySelector('#aiTopicInput').value.trim();
      if (topic.length < 3) return window.showToast(i18n.t('enter_ai_topic'));
      button.disabled = true;
      button.innerHTML = `${icon('sparkles')} ${i18n.t('generating')}`;
      try {
        game.title = container.querySelector('#gameTitleInput').value.trim();
        game.description = container.querySelector('#gameDescInput').value.trim();
        game.timeLimit = Number(container.querySelector('#gameTimeLimitSelect').value);
        game.coverImage = container.querySelector('input[name="gameCover"]:checked')?.value || coverChoices[0];
        game.requirePlayerLogin = container.querySelector('#requirePlayerLoginChk').checked;
        const generated = await dbSim.generateQuiz(
          topic,
          Number(container.querySelector('#aiQuestionCount').value),
          container.querySelector('#aiDifficulty').value,
          i18n.getLang()
        );
        game.questions = generated.questions.map(question => ({ ...question, timeLimit: game.timeLimit }));
        if (!game.title) game.title = generated.title;
        render();
        window.showToast(`Đã tạo ${game.questions.length} câu hỏi. Hãy kiểm tra trước khi lưu.`);
      } catch (error) {
        button.disabled = false;
        button.innerHTML = `${icon('sparkles')} ${i18n.t('generate_draft')}`;
        window.showToast(error.message);
      }
    });
  };

  const renderQuestions = () => {
    const questionsContainer = container.querySelector('#questionsContainer');
    questionsContainer.innerHTML = `
      <h2 style="font-size:18px; font-weight:700; margin-bottom:8px;">${i18n.t('question_list')} (${game.questions.length}/${MAX_QUESTIONS})</h2>
      <p class="quiz-duration-guidance ${game.questions.length > RECOMMENDED_QUESTIONS ? 'is-warning' : ''}">${icon('clock')} ${i18n.t('quiz_length_recommendation')}</p>
    `;

    game.questions.forEach((q, idx) => {
      const questionEditing = editingQuestions.has(q.id);
      const textAccess = key => questionEditing && activeFields.has(key) ? '' : 'readonly aria-readonly="true"';
      const qCard = document.createElement('details');
      qCard.className = `card question-disclosure ${questionEditing ? 'section-is-editing' : ''}`;
      qCard.style.marginBottom = '20px';
      qCard.dataset.idx = idx;
      if (expandedQuestions.has(q.id)) qCard.open = true;

      qCard.innerHTML = `
        <summary class="question-summary">
          <span><strong>${i18n.t('question')} ${idx + 1}</strong><small>${escapeHTML(q.questionText || i18n.t('question_placeholder'))}</small></span>
          <span class="question-summary-actions">${icon('chevronDown')}</span>
        </summary>
        <div class="question-disclosure-content">
        <div class="section-edit-toolbar">
          <label class="editing-mode-control editing-mode-compact question-edit-control" for="questionEditingSwitch-${q.id}"><span>${i18n.t('editing_mode')}</span><input id="questionEditingSwitch-${q.id}" class="question-editing-switch" data-question-id="${q.id}" type="checkbox" ${questionEditing ? 'checked' : ''}><i aria-hidden="true"></i></label>
          ${questionEditing && game.questions.length > 1 ? `
            <button class="delete-action delete-question-btn" data-idx="${idx}" aria-label="${i18n.t('delete_question')}" title="${i18n.t('delete_question')}">
              ${icon('trash')} <span>${i18n.t('delete_question')}</span>
            </button>
          ` : ''}
        </div>

        <div class="form-group inline-edit-field" data-edit-field="question.${q.id}.text">
          <label class="form-label">${i18n.t('question_content')}</label>
          <input type="text" class="input-field question-text-input" value="${escapeHTML(q.questionText)}" placeholder="${i18n.t('question_placeholder')}" ${textAccess(`question.${q.id}.text`)} required />
        </div>

        <!-- Answers section -->
        <label class="form-label">${i18n.t('answer_options')}</label>
        <div class="options-container" style="display:grid; grid-template-columns:1fr; gap:12px;">
          <!-- Options will render dynamically -->
        </div>

        <!-- Explanation section -->
        <div class="form-group inline-edit-field" data-edit-field="question.${q.id}.explanation" style="margin-top:20px; margin-bottom:0;">
          <label class="form-label">${i18n.t('explanation')}</label>
          <textarea class="input-field question-explanation-input" style="height:60px; resize:none;" placeholder="${i18n.t('explanation_placeholder')}" ${textAccess(`question.${q.id}.explanation`)}>${escapeHTML(q.explanation || '')}</textarea>
        </div>
        </div>
      `;

      renderOptions(qCard, q, idx);
      questionsContainer.appendChild(qCard);
      qCard.addEventListener('toggle', () => qCard.open ? expandedQuestions.add(q.id) : expandedQuestions.delete(q.id));
    });

    questionsContainer.querySelectorAll('.question-editing-switch').forEach(input => input.addEventListener('change', event => {
      const questionId = event.currentTarget.dataset.questionId;
      if (!event.currentTarget.checked && editingQuestions.has(questionId)) syncGameDraft();
      if (event.currentTarget.checked) {
        editingQuestions.add(questionId);
        expandedQuestions.add(questionId);
      } else {
        editingQuestions.delete(questionId);
        [...activeFields].filter(key => key.startsWith(`question.${questionId}.`)).forEach(key => activeFields.delete(key));
      }
      render();
    }));

    // Bind question-specific events
    questionsContainer.querySelectorAll('.delete-question-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const index = parseInt(e.currentTarget.dataset.idx);
        game.questions.splice(index, 1);
        render(); // Re-render complete page to update counts
      });
    });
  };

  const renderOptions = (qCard, q, qIdx) => {
    const questionEditing = editingQuestions.has(q.id);
    const optContainer = qCard.querySelector('.options-container');
    optContainer.innerHTML = '';

    q.options.forEach((opt, optIdx) => {
      const isCorrect = q.correctOptions.includes(opt.id);
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '12px';

      // Design colors matching F1 play styles
      const colors = ['#ff3b5c', '#0070f3', '#f5a623', '#00df89'];
      const borderTheme = colors[optIdx] || 'var(--color-border)';
      const correctKey = `question.${q.id}.correct`;
      const optionKey = `question.${q.id}.option.${opt.id}`;

      row.innerHTML = `
        <span class="inline-edit-field option-radio-field" data-edit-field="${correctKey}"><input type="radio" name="correct_rad_${q.id}" class="option-correct-rad" data-opt-id="${opt.id}" ${isCorrect ? 'checked' : ''} ${questionEditing && activeFields.has(correctKey) ? '' : 'disabled'} style="width:20px; height:20px; accent-color:var(--color-primary); cursor:pointer;" required /><b style="font-weight:800; color:${borderTheme}; width:16px;">${opt.id}</b></span>
        <div class="inline-edit-field option-text-field" data-edit-field="${optionKey}"><input type="text" class="input-field option-text-input" data-opt-idx="${optIdx}" value="${escapeHTML(opt.text)}" placeholder="${i18n.t('answer_placeholder')}" ${questionEditing && activeFields.has(optionKey) ? '' : 'readonly aria-readonly="true"'} style="border-left: 4px solid ${borderTheme}; flex:1;" required /></div>
        ${questionEditing && q.options.length > 2 ? `
          <button type="button" class="delete-action delete-action-icon delete-option-btn" data-opt-idx="${optIdx}" aria-label="${i18n.t('delete_option')}" title="${i18n.t('delete_option')}">${icon('trash')}</button>
        ` : ''}
      `;

      optContainer.appendChild(row);
    });

    // Add option button if options < 4
    if (questionEditing && q.options.length < 4) {
      const addBtnContainer = document.createElement('div');
      addBtnContainer.style.marginTop = '8px';
      addBtnContainer.innerHTML = `
        <button type="button" class="btn btn-secondary add-option-btn" style="padding: 8px 16px; font-size:13px; border-style:dashed;">
          ${icon('plus')} ${i18n.t('add_option')}
        </button>
      `;
      optContainer.appendChild(addBtnContainer);

      addBtnContainer.querySelector('.add-option-btn').addEventListener('click', () => {
        const nextId = String.fromCharCode(65 + q.options.length);
        q.options.push({ id: nextId, text: "" });
        render();
      });
    }

    // Bind delete option handler
    qCard.querySelectorAll('.delete-option-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const optIdx = parseInt(e.currentTarget.dataset.optIdx);
        q.options.splice(optIdx, 1);

        // Re-index options IDs to A, B, C, D
        q.options.forEach((opt, idx) => {
          opt.id = String.fromCharCode(65 + idx);
        });

        // Safeguard correct answer mapping
        const deletedId = String.fromCharCode(65 + optIdx);
        if (q.correctOptions.includes(deletedId)) {
          q.correctOptions = ["A"]; // fallback
        }

        render();
      });
    });
  };

  const addQuestion = () => {
    if (game.questions.length >= MAX_QUESTIONS) {
      window.showToast(i18n.t('max_questions'));
      return;
    }

    game.questions.push({
      id: "q_" + Math.random().toString(36).substr(2, 9),
      type: "multiple_choice", // internal default
      questionText: "",
      timeLimit: game.timeLimit,
      options: [
        { id: "A", text: "" },
        { id: "B", text: "" },
        { id: "C", text: "" },
        { id: "D", text: "" }
      ],
      correctOptions: ["A"],
      explanation: ""
    });
    
    render(); // Re-render to update counts and warnings
  };

  const syncGameDraft = () => {
    const title = container.querySelector('#gameTitleInput');
    if (!title) return;
    game.title = title.value.trim();
    game.description = container.querySelector('#gameDescInput').value.trim();
    game.timeLimit = parseInt(container.querySelector('#gameTimeLimitSelect').value);
    game.coverImage = container.querySelector('input[name="gameCover"]:checked')?.value || coverChoices[0];
    game.requirePlayerLogin = container.querySelector('#requirePlayerLoginChk')?.checked || false;
    container.querySelectorAll('#questionsContainer > .card').forEach(card => {
      const question = game.questions[parseInt(card.dataset.idx)];
      question.questionText = card.querySelector('.question-text-input').value.trim();
      question.explanation = card.querySelector('.question-explanation-input').value.trim();
      question.timeLimit = game.timeLimit;
      card.querySelectorAll('.option-text-input').forEach(input => {
        question.options[parseInt(input.dataset.optIdx)].text = input.value.trim();
      });
      const selected = card.querySelector('.option-correct-rad:checked');
      if (selected) question.correctOptions = [selected.dataset.optId];
    });
  };

  const saveGameData = async () => {
    if (!basicEditing && editingQuestions.size === 0) return;
    // 1. Gather Meta Info
    game.title = container.querySelector('#gameTitleInput').value.trim();
    game.description = container.querySelector('#gameDescInput').value.trim();
    game.timeLimit = parseInt(container.querySelector('#gameTimeLimitSelect').value);
    game.coverImage = container.querySelector('input[name="gameCover"]:checked')?.value || coverChoices[0];
    
    const loginChk = container.querySelector('#requirePlayerLoginChk');
    game.requirePlayerLogin = loginChk ? loginChk.checked : false;

    if (!game.title) {
      window.showToast("Tên trò chơi không được để trống!");
      return;
    }

    if (game.questions.length > MAX_QUESTIONS) {
      window.showToast(i18n.t('max_questions'));
      return;
    }

    // 2. Gather Questions Info
    const qCards = container.querySelectorAll('#questionsContainer > .card');
    let hasError = false;

    qCards.forEach(card => {
      const idx = parseInt(card.dataset.idx);
      const q = game.questions[idx];
      
      q.questionText = card.querySelector('.question-text-input').value.trim();
      q.timeLimit = game.timeLimit;
      q.explanation = card.querySelector('.question-explanation-input').value.trim();
      q.type = "multiple_choice"; // force standard choice type

      if (!q.questionText) {
        window.showToast(`Nội dung câu hỏi ${idx + 1} không được để trống!`);
        hasError = true;
        return;
      }

      // Gather Options texts
      const optInputs = card.querySelectorAll('.option-text-input');
      optInputs.forEach(input => {
        const optIdx = parseInt(input.dataset.optIdx);
        q.options[optIdx].text = input.value.trim();
        if (!q.options[optIdx].text) {
          window.showToast(`Lựa chọn ${q.options[optIdx].id} của câu hỏi ${idx + 1} không được để trống!`);
          hasError = true;
        }
      });

      // Gather Checked Correct Option (Single Radio Selection)
      const selectedRad = card.querySelector('.option-correct-rad:checked');
      q.correctOptions = selectedRad ? [selectedRad.dataset.optId] : [];

      if (q.correctOptions.length === 0) {
        window.showToast(`Câu hỏi ${idx + 1} cần chọn đáp án đúng!`);
        hasError = true;
      }
    });

    if (hasError) return;

    try {
      await dbSim.saveGame(game);
      window.showToast(i18n.t('game_saved'));
      window.location.hash = '#/dashboard';
    } catch (err) {
      window.showToast("Không thể lưu trò chơi: " + err.message);
    }
  };

  render();
};
